import React from 'react';
import ReactDOM from 'react-dom';
import classnames from 'classnames';
import { CSSTransition } from 'react-transition-group';
import Theme from "./Theme.js";
import S from './PortalOverlay.css';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
const ANIMATIONS = {
  slideFromRight: {
    enter: S.slideFromRightEnter,
    enterActive: S.slideFromRightEnterActive,
    appear: S.slideFromRightEnter,
    appearActive: S.slideFromRightEnterActive,
    exit: S.slideFromRightExit,
    exitActive: S.slideFromRightExitActive
  },
  slideFromLeft: {
    enter: S.slideFromLeftEnter,
    enterActive: S.slideFromLeftEnterActive,
    appear: S.slideFromLeftEnter,
    appearActive: S.slideFromLeftEnterActive,
    exit: S.slideFromLeftExit,
    exitActive: S.slideFromLeftExitActive
  },
  slideFromBottom: {
    enter: S.slideFromBottomEnter,
    enterActive: S.slideFromBottomEnterActive,
    appear: S.slideFromBottomEnter,
    appearActive: S.slideFromBottomEnterActive,
    exit: S.slideFromBottomExit,
    exitActive: S.slideFromBottomExitActive
  },
  slideFromTop: {
    enter: S.slideFromTopEnter,
    enterActive: S.slideFromTopEnterActive,
    appear: S.slideFromTopEnter,
    appearActive: S.slideFromTopEnterActive,
    exit: S.slideFromTopExit,
    exitActive: S.slideFromTopExitActive
  },
  fade: {
    appear: S.fadeEnter,
    appearActive: S.fadeEnterActive,
    enter: S.fadeEnter,
    enterActive: S.fadeEnterActive,
    exit: S.fadeExit,
    exitActive: S.fadeExitActive
  }
};
function animationFor(animation) {
  return ANIMATIONS[animation] || ANIMATIONS.fade;
}
function sidebarPosition(pos) {
  switch (pos) {
    case 'left':
      return S.sidebarLeft;
    case 'right':
      return S.sidebarRight;
    case 'top':
      return S.sidebarTop;
    case 'bottom':
      return S.sidebarBottom;
    case 'center':
      return S.sidebarCenter;
    case 'full':
      return S.sidebarFull;
    default:
      return null;
  }
}
class OnlyClickMe extends React.Component {
  constructor(props) {
    super(props);
    this.ref = /*#__PURE__*/React.createRef();
  }
  onClick = e => {
    const {
      onClick
    } = this.props;
    if (onClick && e.target && e.target === this.ref.current) {
      onClick(e);
    }
  };
  render() {
    // eslint-disable-next-line no-unused-vars
    const {
      onClick,
      className,
      ...rest
    } = this.props;
    return /*#__PURE__*/_jsx("div", {
      className: classnames(S.onlyClickMe, className),
      ref: this.ref,
      onClick: this.onClick,
      ...rest
    });
  }
}

/**
 * The PortalOverlay will install into a targeted element ID, a panel
 * that can appear over other elements. The Portal element can be used
 * to create element to target.
 **/
export default class PortalOverlay extends React.Component {
  static contextType = Theme.Context;
  onKey = event => {
    const {
      onClose,
      escapeWillClose = true
    } = this.props;
    if (escapeWillClose && typeof onClose === 'function' && event.keyCode === 27) {
      onClose();
    }
  };
  componentDidMount() {
    document.addEventListener('keydown', this.onKey, false);
  }
  componentWillUnmount() {
    document.removeEventListener('keydown', this.onKey, false);
  }
  render() {
    const theme = this.context;
    const {
      targetId,
      showBackground = true,
      show = false,
      position,
      body = true,
      clickWillClose = true,
      onClose,
      animation,
      children,
      managed = false,
      variant,
      className,
      backgroundClassName
    } = theme.props('Portal', this.props);
    let target = targetId == null ? null : document.getElementById(targetId);
    if (target == null) {
      if (body) {
        target = document.body;
      } else {
        return null;
      }
    }
    const c = position == null ? /*#__PURE__*/_jsx(OnlyClickMe, {
      children: children
    }) : position === 'center' ? /*#__PURE__*/_jsx(OnlyClickMe, {
      onClick: clickWillClose ? onClose : null,
      children: children
    }) : children;
    /*
    * The timeouts here must align with the animations in the css. Try to use props/variant to
    * set the value appropriately.
    */

    const content = /*#__PURE__*/_jsxs(React.Fragment, {
      children: [showBackground && /*#__PURE__*/_jsx(CSSTransition, {
        in: show,
        timeout: 300,
        appear: true,
        classNames: ANIMATIONS.fade,
        mountOnEnter: true,
        unmountOnExit: true,
        children: /*#__PURE__*/_jsx("div", {
          className: theme.classes('Portal', 'portal', variant, S.background, backgroundClassName),
          onClick: clickWillClose ? e => {
            consume(e);
            onClose?.(e);
          } : consume,
          onTouchEnd: clickWillClose ? e => {
            consume(e);
            onClose?.(e);
          } : consume,
          onDoubleClick: consume,
          onTouchStart: consume,
          onMouseDown: consume,
          onMouseUp: consume,
          onContextMenu: consume
        })
      }), /*#__PURE__*/_jsx(CSSTransition, {
        in: show,
        timeout: 300,
        appear: true,
        className: classnames(S.sidebar, sidebarPosition(position), className),
        classNames: animationFor(animation),
        onEnter: this.onEnter,
        onExited: this.onExited,
        mountOnEnter: true,
        unmountOnExit: true,
        children: c
      })]
    });
    if (managed) {
      return content;
    }
    return /*#__PURE__*/ReactDOM.createPortal(content, target);
  }
}
function consume(e) {
  if (e?.preventDefault) {
    e.preventDefault();
  }
  if (e?.stopPropagation) {
    e.stopPropagation();
  }
}